import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { translate } from '@thiememeulenhoff/translations';
import { HasSlotController } from '../../controllers';
import { TmElement } from '../../custom-element.helper';

export class TmDialogElement extends TmElement {
  @query('dialog')
  dialog!: HTMLDialogElement;

  @query('slot[name="trigger"]')
  triggerSlot!: HTMLSlotElement;

  @query('#trigger')
  trigger!: HTMLButtonElement;

  @query('#content')
  content!: HTMLDivElement;

  @property({ type: Boolean, reflect: true })
  contained = false;

  @property({ type: Boolean, attribute: 'keep-alive' })
  keepAlive = false;

  @property({ type: Boolean, reflect: true })
  disabled?: boolean;

  @property({ type: Boolean, reflect: true })
  loading?: false;

  @property({ type: Boolean, attribute: 'custom-trigger' })
  customTrigger?: false;

  extraControls() {
    return html``;
  }

  #backdropClickEnabled = true;

  #hasSlotController = new HasSlotController(this, 'header');

  protected closeButton() {
    if (this.contained) {
      return this.title || this.#hasSlotController.test('header')
        ? nothing
        : html`<div class="close-button-wrapper" part="close-button-wrapper">
            <button
              part="close-button"
              class="close-contained"
              data-test="dialog-close-button"
              aria-label="close"
              @click=${this.close}
              @touchstart=${this.close}
            >
              <a-icon name="close"></a-icon>
            </button>
          </div>`;
    }
    return html`<button
      part="close-button"
      aria-label="close"
      class="close"
      @click=${this.close}
      @touchstart=${this.close}
    >
      <a-icon name="close"></a-icon>
    </button>`;
  }

  protected header() {
    return html`<header part="header-container">
      <div part="header">
        <slot name="header">${unsafeHTML(this.title)}</slot>
      </div>
      ${this.contained
        ? html`<button
            part="close-button"
            @click=${this.close}
            @touchstart=${this.close}
            aria-label="${translate('general.action.close')}"
          >
            <a-icon name="close"></a-icon>
          </button>`
        : nothing}
    </header>`;
  }

  protected dialogContent() {
    return html`<container
      part="container"
      class=${classMap({
        'has-header': this.title || this.#hasSlotController.test('header'),
      })}
    >
      ${this.header()}
      <main part="main" id="content"><slot></slot></main>
      ${this.extraControls()} ${this.closeButton()}
    </container>`;
  }

  protected loaderContent() {
    return this.loading
      ? html`
          <div class="loaderBackground"></div>
          <div class="loading" part="loader" role="loader">
            <a-loader></a-loader>
          </div>
        `
      : nothing;
  }

  protected modalTemplate() {
    return html`
      <dialog
        data-test="dialog"
        part="dialog"
        role="dialog"
        aria-label="dialog"
        @mousedown=${this.#handleBackDropClick}
        @touchstart=${this.#handleBackDropClick}
        tabindex="-1"
      >
        ${this.allContents()}
      </dialog>
    `;
  }

  protected allContents() {
    return html` ${this.dialogContent()} ${this.loaderContent()} `;
  }

  override render() {
    return html`
      <div
        class="trigger-container"
        @click=${this.open}
        @keydown=${this.#handleKeyDown}
      >
        <slot name="trigger"></slot>
      </div>
      ${this.modalTemplate()}
    `;
  }

  #handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.open();
    }
  }

  override async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    // Just open it up when there is no trigger
    // This can be handy to add a modal programmatically,
    // or if you need to open up the modal directly from a template.
    // Custom trigger can be used when there is no trigger element, but the dialog is opened programmatically.
    if (this.#noTrigger() && !this.customTrigger) {
      this.open();
    }

    this.dialog.addEventListener('close', () => this.onClose());
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.dialog?.removeEventListener('close', this.onClose);
  }

  open() {
    this.dispatchLocalEvent({
      eventName: 'open',
    });

    this.dialog.inert = true;
    this.dialog.showModal();
    this.dialog.inert = false;
  }

  close(closeReason?: string) {
    if (!this.dialog.open || this.disabled) {
      return;
    }

    this.dispatchGlobalEvent({
      eventName: 'closed-dialog',
    });

    this.dialog.classList.add('hide');
    this.dialog.addEventListener('animationend', (event) => {
      if (
        event.animationName === 'fade-out' ||
        event.animationName.includes('slide-out')
      ) {
        this.dialog.classList.remove('hide');
        this.dialog.close();

        if (closeReason) {
          this.dispatchGlobalEvent({
            eventName: closeReason,
          });
        }
      }
    });
  }

  onClose() {
    this.dispatchLocalEvent({
      eventName: 'close',
    });

    if (this.#noTrigger() && !this.keepAlive) {
      this.remove();
    }
  }

  get backdropClickEnabled() {
    return this.#backdropClickEnabled;
  }

  set backdropClickEnabled(value: boolean) {
    this.#backdropClickEnabled = value;
  }

  #noTrigger() {
    const assignedElements = this.triggerSlot?.assignedElements({
      flatten: true,
    });
    return assignedElements?.length === 0;
  }

  #handleBackDropClick(event: MouseEvent) {
    if (this.#clickIsOnBackdrop(event) && this.#backdropClickEnabled) {
      this.close();
    }
  }

  #clickIsOnBackdrop(event: MouseEvent) {
    // Because we have an inner container ".content" we can say with
    // confidence that a click on `this.modal` is a click on the backdrop.
    return event.target === this.dialog;
  }
}
